import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { APP_CONFIG } from '../../config/appConfig';
import { formatChinaDateTime, toChinaDateKey } from '../../utils/chinaTime';

const AI_NAME = APP_CONFIG.aiName;

export type MedalRecord = {
  badgeId: string;
  badgeName: string;
  badgeIcon?: string | null;
  assignmentTitle?: string;
  feedbackSummary?: string;
};

export type MedalOverviewRecord = {
  studentId: string;
  studentName: string;
  avatar?: string | null;
  medals: MedalRecord[];
};

export type ImageFeedback = {
  encouragement?: string;
  recognitionSummary?: string;
  medalTitle?: string;
  starRatings?: Record<string, number>;
};

export type ImagePeerReview = {
  id: string;
  reviewerName: string;
  comment: string;
  createdAt?: string;
};

export type ImageRecord = {
  id: string;
  studentId: string;
  studentName: string;
  avatar?: string | null;
  imageUrl: string;
  thumbnailUrl: string;
  sceneGroup?: string;
  sceneLabel?: string;
  sceneName?: string;
  createdAt?: string;
  feedback?: ImageFeedback;
  peerReviews?: ImagePeerReview[];
};

export type PreparationPoster = {
  avatarId?: string;
  teammateNames: string[];
  sceneName: string;
  isCustomScene?: boolean;
  equipmentNames: string[];
  voiceSummary: string;
  blessing: string;
};

export type PreparationPosterRecord = {
  id: string;
  studentId: string;
  studentName: string;
  avatar?: string | null;
  sessionId?: string;
  assignmentId?: string;
  assignmentTitle?: string;
  teamPhotoUrl?: string;
  createdAt?: string;
  poster: PreparationPoster;
  feedback?: ImageFeedback;
};

export type TeacherLessonRunOption = {
  lessonRunId: string;
  lessonLabel: string;
  startedAt: string;
};

export type TeacherAdventureOverviewResponse = {
  filter?: {
    date: string | null;
    lessonRunId: string | null;
    lessonLabel: string | null;
    availableLessonRuns: TeacherLessonRunOption[];
  };
  medalOverview: MedalOverviewRecord[];
  preparationPosterRecords: PreparationPosterRecord[];
  dangerImageRecords: ImageRecord[];
  escapeImageRecords: ImageRecord[];
};

export type AnalyticsRange = '24h' | '7d' | '30d';
export type TeacherAnalyticsRange = 'today' | 'yesterday' | 'week' | 'all';

export type TeacherAnalyticsFilter = {
  range: TeacherAnalyticsRange | null;
  date: string | null;
  lessonRunId: string | null;
  label: string;
  lessonLabel: string | null;
  availableLessonRuns: TeacherLessonRunOption[];
};

export type CountDistributionItem = {
  name: string;
  count: number;
};

export type BadgeDistributionItem = {
  badgeName: string;
  count: number;
};

export type TermFrequencyItem = {
  term: string;
  count: number;
};

export type RepresentativeWorkItem = {
  id: string;
  type: 'danger' | 'escape';
  studentName: string;
  thumbnailUrl: string;
};

export type ProgressFunnelItem = {
  stageKey: string;
  label: string;
  count: number;
  ratio: number;
};

export type MedalCountDistributionItem = {
  medalCount: number;
  studentCount: number;
};

export type TopStudentItem = {
  studentId: string;
  studentName: string;
  medalCount: number;
};

export type ImageTrendItem = {
  date: string;
  dangerUploads: number;
  escapeUploads: number;
  totalUploads: number;
};

export type StudentDetailItem = {
  studentId: string;
  studentName: string;
  prepared: boolean;
  uploadedDanger: boolean;
  uploadedEscape: boolean;
  medalCount: number;
  lastActiveAt: string;
};

export type StepAnalyticsSummary = {
  totalRuns: number;
  successRate: number;
  timeoutRate: number;
  jsonParseFailureRate: number;
  avgDurationMs: number;
  p95DurationMs: number;
};

export type ModelHitRateItem = {
  model: string;
  count: number;
  ratio: number;
};

export type FailureBreakdownItem = {
  errorType: string;
  count: number;
  ratio: number;
};

export type AnalyticsTimelinePoint = {
  bucket: string;
  totalRuns: number;
  successRate: number;
  avgDurationMs: number;
};

export type PipelineBreakdownItem = {
  pipelineMode: string;
  totalRuns: number;
  successRate: number;
};

export type TeacherAdventureAnalyticsResponse = {
  filter: TeacherAnalyticsFilter;
  classroomBoard: {
    teammateDistribution: CountDistributionItem[];
    sceneDistribution: CountDistributionItem[];
    equipmentDistribution: CountDistributionItem[];
    dangerWorksByScene: Array<{
      sceneGroup: string;
      sceneLabel: string;
      items: ImageRecord[];
    }>;
    escapeWorks: ImageRecord[];
  };
};

export type TeacherAdventureAiAnalyticsResponse = {
  summary: {
    danger: StepAnalyticsSummary;
    escape: StepAnalyticsSummary;
  };
  modelHitRates: {
    danger: {
      ocr: ModelHitRateItem[];
      directVision: ModelHitRateItem[];
    };
    escape: {
      ocr: ModelHitRateItem[];
      directVision: ModelHitRateItem[];
    };
  };
  failureBreakdown: {
    danger: FailureBreakdownItem[];
    escape: FailureBreakdownItem[];
  };
  timeline: {
    danger: AnalyticsTimelinePoint[];
    escape: AnalyticsTimelinePoint[];
  };
  pipelineBreakdown: {
    danger: PipelineBreakdownItem[];
    escape: PipelineBreakdownItem[];
  };
  guideChatSummaries: Array<{
    sessionId?: string;
    assignmentId?: string;
    studentId: string;
    studentName: string;
    misconceptionTags: string[];
    summary: string;
    updatedAt: string;
  }>;
};

export const getAssetUrl = (value?: string) => {
  if (!value) {
    return '';
  }

  if (value.startsWith('data:')) {
    return value;
  }

  const maybeApi = api as typeof api & { defaults?: { baseURL?: string } };
  const rawBaseUrl = maybeApi.defaults?.baseURL ?? '';
  const baseUrl = rawBaseUrl.replace(/\/api\/?$/, '/');
  const apiOrigin = (() => {
    try {
      return new URL(baseUrl).origin;
    } catch (_error) {
      return '';
    }
  })();

  if (value.startsWith('/uploads/')) {
    return apiOrigin ? new URL(value, `${apiOrigin}/`).toString() : value;
  }

  if (/^https?:\/\//.test(value)) {
    try {
      const parsedUrl = new URL(value);
      const isLocalUploadHost =
        /^localhost$/i.test(parsedUrl.hostname) ||
        /^127(?:\.\d{1,3}){3}$/.test(parsedUrl.hostname) ||
        parsedUrl.hostname === '0.0.0.0';

      if (apiOrigin && parsedUrl.pathname.startsWith('/uploads/') && isLocalUploadHost) {
        return new URL(parsedUrl.pathname, `${apiOrigin}/`).toString();
      }

      return value;
    } catch (_error) {
      return value;
    }
  }

  if (value.startsWith('uploads/')) {
    return apiOrigin ? new URL(value, `${apiOrigin}/`).toString() : `/${value}`;
  }

  try {
    return new URL(value.replace(/^\//, ''), baseUrl).toString();
  } catch (_error) {
    return value;
  }
};

export const formatTeacherUploadTime = (value?: string) => {
  if (!value) {
    return '未记录时间';
  }

  return formatChinaDateTime(value) || value;
};

const toNaturalDateKey = (value?: string) => {
  return toChinaDateKey(value);
};

const normalizeViewerRotation = (rotation: number) => ((rotation % 360) + 360) % 360;
export const normalizeViewerScale = (scale: number) => {
  const rounded = Math.round(scale * 100) / 100;
  return Math.min(2.5, Math.max(1, rounded));
};
export const buildViewerTransform = (rotation: number, scale = 1) =>
  scale === 1 ? `rotate(${rotation}deg)` : `rotate(${rotation}deg) scale(${scale})`;
const IMAGE_SCENE_GROUPS = [
  { key: 'desert', label: '茫茫大漠' },
  { key: 'rainforest', label: '热带雨林' },
  { key: 'island', label: '海中荒岛' },
  { key: 'cave', label: '幽深洞穴' },
  { key: 'glacier', label: '冰川' },
  { key: 'custom', label: '自定义场景' },
] as const;

const RotationActionButton: React.FC<{
  label: string;
  onClick: () => void;
  icon: 'left' | 'right' | 'reset' | 'zoomIn' | 'zoomOut' | 'zoomReset';
}> = ({ label, onClick, icon }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/80 bg-white/92 text-slate-700 shadow-lg backdrop-blur transition hover:scale-[1.03] hover:border-slate-300 hover:text-slate-900"
  >
    {icon === 'left' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M8 7L3 12l5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 12h9a7 7 0 0 1 7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : icon === 'right' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M16 7l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 12h-9a7 7 0 0 0-7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : icon === 'zoomIn' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M11 8v6M8 11h6" strokeLinecap="round" />
      </svg>
    ) : icon === 'zoomOut' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M8 11h6" strokeLinecap="round" />
      </svg>
    ) : icon === 'zoomReset' ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="6" />
        <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
        <path d="M8.5 8.5l5 5M13.5 8.5l-5 5" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M20 11a8 8 0 1 0 2 5.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 4v7h-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const RotationToolbar: React.FC<{
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onReset: () => void;
  className?: string;
}> = ({ onRotateLeft, onRotateRight, onReset, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <RotationActionButton label="向左旋转" onClick={onRotateLeft} icon="left" />
    <RotationActionButton label="回正" onClick={onReset} icon="reset" />
    <RotationActionButton label="向右旋转" onClick={onRotateRight} icon="right" />
  </div>
);

export const ZoomToolbar: React.FC<{
  onZoomOut: () => void;
  onZoomIn: () => void;
  onReset: () => void;
  className?: string;
}> = ({ onZoomOut, onZoomIn, onReset, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <RotationActionButton label="缩小图片" onClick={onZoomOut} icon="zoomOut" />
    <RotationActionButton label="重置缩放" onClick={onReset} icon="zoomReset" />
    <RotationActionButton label="放大图片" onClick={onZoomIn} icon="zoomIn" />
  </div>
);

export const STAR_LABELS: Record<string, string> = {
  imaginationReasonableness: '想象丰富合理',
  dilemmaSpecificity: '困境描写具体',
  survivalMethodSpecificity: '求生方法具体',
  crisisProgression: '想象丰富合理',
  detailMagnification: '困境描写具体',
  teamworkCoordination: '求生方法具体',
};

export const SectionEmpty: React.FC<{ text: string }> = ({ text }) => (
  <div className="rounded-[1.5rem] border border-dashed border-stone-300 bg-stone-50 px-6 py-10 text-center text-sm text-slate-500">
    {text}
  </div>
);

export const TeacherPageShell: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
  backToTeacherHome?: boolean;
}> = ({ title, description, children, backToTeacherHome = true }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-100 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="rounded-[2rem] border border-stone-200 bg-white px-6 py-6 shadow-sm sm:px-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold tracking-[0.24em] text-amber-700">TEACHER CONSOLE</div>
              <h1 className="mt-2 text-3xl font-black text-slate-900">{title}</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {user?.username}，{description}
              </p>
            </div>
            <div className="flex gap-3">
              {backToTeacherHome ? (
                <button
                  onClick={() => navigate('/teacher')}
                  className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
                >
                  返回首页
                </button>
              ) : null}
              <button
                onClick={() => {
                  dispatch(logout());
                  navigate('/teacher/login');
                }}
                className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
              >
                退出登录
              </button>
            </div>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
};

const buildTeacherOverviewParams = (selectedDate?: string, selectedLessonRunId?: string) => {
  const params: Record<string, string> = {};
  if (selectedDate) {
    params.date = selectedDate;
  }
  if (selectedLessonRunId) {
    params.lessonRunId = selectedLessonRunId;
  }

  return Object.keys(params).length > 0 ? params : undefined;
};

const normalizeOverviewFilter = (
  filter: TeacherAdventureOverviewResponse['filter'] | undefined,
  selectedDate?: string,
  selectedLessonRunId?: string
) => ({
  date: filter?.date ?? selectedDate ?? null,
  lessonRunId: filter?.lessonRunId ?? selectedLessonRunId ?? null,
  lessonLabel: filter?.lessonLabel ?? null,
  availableLessonRuns: Array.isArray(filter?.availableLessonRuns) ? filter?.availableLessonRuns : [],
});

const normalizeAnalyticsFilter = (
  filter: TeacherAdventureAnalyticsResponse['filter'] | undefined,
  fallback: { range?: TeacherAnalyticsRange; date?: string; lessonRunId?: string }
): TeacherAnalyticsFilter => ({
  range: filter?.range ?? (fallback.date || fallback.lessonRunId ? null : fallback.range ?? null),
  date: filter?.date ?? fallback.date ?? null,
  lessonRunId: filter?.lessonRunId ?? fallback.lessonRunId ?? null,
  label: filter?.label ?? fallback.date ?? '当前课次',
  lessonLabel: filter?.lessonLabel ?? null,
  availableLessonRuns: Array.isArray(filter?.availableLessonRuns) ? filter?.availableLessonRuns : [],
});

export const useTeacherAdventureOverview = (selectedDate?: string, selectedLessonRunId?: string) => {
  const [overview, setOverview] = useState<TeacherAdventureOverviewResponse>({
    filter: {
      date: null,
      lessonRunId: null,
      lessonLabel: null,
      availableLessonRuns: [],
    },
    medalOverview: [],
    preparationPosterRecords: [],
    dangerImageRecords: [],
    escapeImageRecords: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadOverview = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setIsLoading(true);
      setLoadError('');
    }

    try {
      const response = await api.get<TeacherAdventureOverviewResponse>(
        '/submissions/teacher-adventure-overview',
        {
          params: buildTeacherOverviewParams(selectedDate, selectedLessonRunId),
        }
      );

      setOverview({
        filter: normalizeOverviewFilter(response.data?.filter, selectedDate, selectedLessonRunId),
        medalOverview: Array.isArray(response.data?.medalOverview) ? response.data.medalOverview : [],
        preparationPosterRecords: Array.isArray(response.data?.preparationPosterRecords)
          ? response.data.preparationPosterRecords
          : [],
        dangerImageRecords: Array.isArray(response.data?.dangerImageRecords)
          ? response.data.dangerImageRecords
          : [],
        escapeImageRecords: Array.isArray(response.data?.escapeImageRecords)
          ? response.data.escapeImageRecords
          : [],
      });
    } catch (error) {
      console.error(error);
      setLoadError('读取教师端概览失败，请稍后重试');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    let active = true;

    const loadWithGuard = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await api.get<TeacherAdventureOverviewResponse>(
          '/submissions/teacher-adventure-overview',
          {
            params: buildTeacherOverviewParams(selectedDate, selectedLessonRunId),
          }
        );

        if (!active) {
          return;
        }

        setOverview({
          filter: normalizeOverviewFilter(response.data?.filter, selectedDate, selectedLessonRunId),
          medalOverview: Array.isArray(response.data?.medalOverview) ? response.data.medalOverview : [],
          preparationPosterRecords: Array.isArray(response.data?.preparationPosterRecords)
            ? response.data.preparationPosterRecords
            : [],
          dangerImageRecords: Array.isArray(response.data?.dangerImageRecords)
            ? response.data.dangerImageRecords
            : [],
          escapeImageRecords: Array.isArray(response.data?.escapeImageRecords)
            ? response.data.escapeImageRecords
            : [],
        });
      } catch (error) {
        if (active) {
          console.error(error);
          setLoadError('读取教师端概览失败，请稍后重试');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadWithGuard();

    return () => {
      active = false;
    };
  }, [selectedDate, selectedLessonRunId]);

  return { overview, isLoading, loadError, reloadOverview: loadOverview };
};

export const useTeacherAdventureAnalytics = (params: {
  range?: TeacherAnalyticsRange;
  date?: string;
  lessonRunId?: string;
}) => {
  const [analytics, setAnalytics] = useState<TeacherAdventureAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const queryString = useMemo(() => {
    if (params.lessonRunId) {
      const query = new URLSearchParams();
      if (params.date) {
        query.set('date', params.date);
      }
      query.set('lessonRunId', params.lessonRunId);
      return query.toString();
    }

    if (params.date) {
      return `date=${encodeURIComponent(params.date)}`;
    }

    if (params.range) {
      return `range=${params.range}`;
    }

    return '';
  }, [params.date, params.lessonRunId, params.range]);

  const loadAnalytics = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setIsLoading(true);
      setLoadError('');
    }

    const response = await api.get<TeacherAdventureAnalyticsResponse>(
      `/submissions/teacher-adventure-analytics${queryString ? `?${queryString}` : ''}`
    );

    setAnalytics({
      ...response.data,
      filter: normalizeAnalyticsFilter(response.data?.filter, params),
    });
  };

  useEffect(() => {
    let active = true;

    const loadWithGuard = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await api.get<TeacherAdventureAnalyticsResponse>(
          `/submissions/teacher-adventure-analytics${queryString ? `?${queryString}` : ''}`
        );

        if (!active) {
          return;
        }

        setAnalytics({
          ...response.data,
          filter: normalizeAnalyticsFilter(response.data?.filter, params),
        });
      } catch (error) {
        if (active) {
          console.error(error);
          setLoadError('读取统计分析失败，请稍后重试');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadWithGuard();

    return () => {
      active = false;
    };
  }, [params.date, params.lessonRunId, params.range, queryString]);

  const reloadAnalytics = async (options?: { silent?: boolean }) => {
    try {
      await loadAnalytics(options);
    } catch (error) {
      console.error(error);
      setLoadError('读取统计分析失败，请稍后重试');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  return { analytics, isLoading, loadError, reloadAnalytics };
};

export const useTeacherAdventureAiAnalytics = (range: AnalyticsRange) => {
  const [analytics, setAnalytics] = useState<TeacherAdventureAiAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let active = true;

    const loadAnalytics = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const response = await api.get<TeacherAdventureAiAnalyticsResponse>(
          `/submissions/teacher-adventure-ai-analytics?range=${range}`
        );

        if (!active) {
          return;
        }

        setAnalytics(response.data);
      } catch (error) {
        if (active) {
          console.error(error);
          setLoadError('读取 AI 分析失败，请稍后重试');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadAnalytics();

    return () => {
      active = false;
    };
  }, [range]);

  return { analytics, isLoading, loadError };
};

export const TeacherLoadingState: React.FC<{ error?: string; loadingText: string }> = ({
  error,
  loadingText,
}) =>
  error ? (
    <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-12 text-center text-rose-500 shadow-sm">
      {error}
    </div>
  ) : (
    <div className="rounded-[2rem] border border-stone-200 bg-white px-6 py-12 text-center text-slate-500 shadow-sm">
      {loadingText}
    </div>
  );

export const TeacherImageGalleryPage: React.FC<{
  title: string;
  description: string;
  imageLabel: string;
  records: ImageRecord[];
  emptyText: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}> = ({ title, description, imageLabel, records, emptyText, onRefresh, isRefreshing = false }) => {
  const [selectedImageDetail, setSelectedImageDetail] = useState<ImageRecord | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [selectedImageRotation, setSelectedImageRotation] = useState(0);
  const [selectedImageScale, setSelectedImageScale] = useState(1);
  const [previewImageRotation, setPreviewImageRotation] = useState(0);
  const [previewImageScale, setPreviewImageScale] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSceneGroup, setSelectedSceneGroup] = useState('all');
  const dateFilteredRecords = useMemo(() => {
    if (!selectedDate) {
      return records;
    }

    return records.filter((record) => toNaturalDateKey(record.createdAt) === selectedDate);
  }, [records, selectedDate]);
  const sceneCounts = useMemo(() => {
    const counts = new Map<string, number>();
    IMAGE_SCENE_GROUPS.forEach((scene) => counts.set(scene.key, 0));
    dateFilteredRecords.forEach((record) => {
      const key = record.sceneGroup || 'custom';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [dateFilteredRecords]);
  const visibleRecords = useMemo(() => {
    if (selectedSceneGroup === 'all') {
      return dateFilteredRecords;
    }

    return dateFilteredRecords.filter((record) => (record.sceneGroup || 'custom') === selectedSceneGroup);
  }, [dateFilteredRecords, selectedSceneGroup]);

  return (
    <TeacherPageShell title={title} description={description}>
      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">点击卡片查看详情，点击图片查看大图。</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <label className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-slate-600">
              <span className="font-semibold text-slate-500">按日期筛选</span>
              <input
                type="date"
                aria-label="按日期筛选"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                className="rounded-xl border border-stone-200 bg-white px-3 py-1 text-sm text-slate-700 outline-none transition focus:border-slate-400"
              />
            </label>
            {selectedDate ? (
              <button
                type="button"
                onClick={() => setSelectedDate('')}
                className="rounded-full border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-900 hover:text-slate-900"
              >
                清除日期
              </button>
            ) : null}
            {onRefresh ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="rounded-full border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-900 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isRefreshing ? '刷新中...' : '刷新作品'}
              </button>
            ) : null}
            <div className="rounded-full bg-stone-100 px-3 py-1 text-sm font-semibold text-slate-600">
              {visibleRecords.length} 张
            </div>
          </div>
        </div>
        <div className="mb-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedSceneGroup('all')}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              selectedSceneGroup === 'all'
                ? 'bg-slate-900 text-white'
                : 'border border-stone-200 bg-white text-slate-700 hover:border-slate-300'
            }`}
          >
            <span>全部</span>
            <span className={`ml-2 text-xs ${selectedSceneGroup === 'all' ? 'text-white/80' : 'text-slate-400'}`}>
              {dateFilteredRecords.length}
            </span>
          </button>
          {IMAGE_SCENE_GROUPS.map((scene) => (
            <button
              key={scene.key}
              type="button"
              onClick={() => setSelectedSceneGroup(scene.key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                selectedSceneGroup === scene.key
                  ? 'bg-slate-900 text-white'
                  : 'border border-stone-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              <span>{scene.label}</span>
              <span className={`ml-2 text-xs ${selectedSceneGroup === scene.key ? 'text-white/80' : 'text-slate-400'}`}>
                {sceneCounts.get(scene.key) ?? 0}
              </span>
            </button>
          ))}
        </div>

        {visibleRecords.length === 0 ? (
          <SectionEmpty
            text={
              selectedSceneGroup !== 'all'
                ? '这个场景下还没有学生上传图片。'
                : selectedDate
                  ? '这一天还没有学生上传图片。'
                  : emptyText
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleRecords.map((record) => (
              <article
                key={record.id}
                className="overflow-hidden rounded-[1.5rem] border border-stone-200 bg-stone-50 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300"
              >
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImageUrl(getAssetUrl(record.imageUrl));
                    setPreviewImageRotation(0);
                    setPreviewImageScale(1);
                  }}
                  className="block w-full bg-slate-200"
                  aria-label={`预览${record.studentName}的${imageLabel}缩略图`}
                >
                  <img
                    src={getAssetUrl(record.thumbnailUrl || record.imageUrl)}
                    alt={`${record.studentName} 的${imageLabel}缩略图`}
                    className="h-56 w-full object-cover"
                  />
                </button>
                <div className="flex items-center gap-3 px-4 py-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg shadow-sm">
                    {record.avatar || '🧒'}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{record.studentName}</div>
                    <div className="mt-1 text-xs text-slate-500">{formatTeacherUploadTime(record.createdAt)}</div>
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedImageDetail(record);
                      setSelectedImageRotation(0);
                      setSelectedImageScale(1);
                    }}
                    aria-label={`查看${record.studentName}的${imageLabel}详情`}
                    className="w-full rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
                  >
                    查看详情
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selectedImageDetail ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black text-slate-900">{selectedImageDetail.studentName}</h3>
                <p className="mt-1 text-sm text-slate-500">{title}详情</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedImageDetail(null);
                  setSelectedImageRotation(0);
                  setSelectedImageScale(1);
                }}
                className="rounded-2xl border border-stone-300 px-3 py-2 text-sm font-semibold text-slate-600"
              >
                关闭
              </button>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.8fr)]">
              <div className="relative overflow-hidden rounded-[1.75rem] bg-stone-100">
                <ZoomToolbar
                  className="absolute left-4 top-4 z-10"
                  onZoomOut={() => setSelectedImageScale((prev) => normalizeViewerScale(prev - 0.25))}
                  onZoomIn={() =>
                    setSelectedImageScale((prev) => normalizeViewerScale(prev >= 1.25 ? prev + 0.25 : 1.25))
                  }
                  onReset={() => setSelectedImageScale(1)}
                />
                <RotationToolbar
                  className="absolute right-4 top-4 z-10"
                  onRotateLeft={() =>
                    setSelectedImageRotation((prev) => normalizeViewerRotation(prev - 90))
                  }
                  onRotateRight={() =>
                    setSelectedImageRotation((prev) => normalizeViewerRotation(prev + 90))
                  }
                  onReset={() => setSelectedImageRotation(0)}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImageUrl(getAssetUrl(selectedImageDetail.imageUrl));
                    setPreviewImageRotation(selectedImageRotation);
                    setPreviewImageScale(selectedImageScale);
                  }}
                  aria-label={`查看${selectedImageDetail.studentName}的${imageLabel}大图`}
                  className="block w-full"
                >
                  <img
                    src={getAssetUrl(selectedImageDetail.imageUrl)}
                    alt={`${selectedImageDetail.studentName} 的${imageLabel}大图`}
                    className="h-full max-h-[70vh] w-full object-contain transition-transform"
                    style={{
                      transform: buildViewerTransform(selectedImageRotation, selectedImageScale),
                    }}
                  />
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">上传时间</div>
                  <div className="mt-2 text-base font-semibold text-slate-900">
                    {formatTeacherUploadTime(selectedImageDetail.createdAt)}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">学生</div>
                  <div className="mt-2 text-lg font-bold text-slate-900">{selectedImageDetail.studentName}</div>
                </div>

                {selectedImageDetail.feedback?.encouragement ? (
                  <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                    <div className="text-sm font-semibold text-slate-500">{AI_NAME}点评</div>
                    <p className="mt-3 text-sm leading-7 text-slate-700">
                      {selectedImageDetail.feedback.encouragement}
                    </p>
                  </div>
                ) : null}

                {selectedImageDetail.feedback?.starRatings ? (
                  <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                    <div className="text-sm font-semibold text-slate-500">星级结果</div>
                    <div className="mt-3 space-y-3">
                      {Object.entries(selectedImageDetail.feedback.starRatings).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between gap-4">
                          <span className="text-sm text-slate-600">{STAR_LABELS[key] ?? key}</span>
                          <span className="text-amber-500">{'★'.repeat(Math.max(0, Math.round(value)))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {selectedImageDetail.feedback?.medalTitle ? (
                  <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5">
                    <div className="text-sm font-semibold text-amber-700">专属称号</div>
                    <div className="mt-2 text-lg font-bold text-amber-950">
                      {selectedImageDetail.feedback.medalTitle}
                    </div>
                  </div>
                ) : null}

                {selectedImageDetail.peerReviews?.length ? (
                  <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                    <div className="text-sm font-semibold text-slate-500">同学评价</div>
                    <div className="mt-3 space-y-3">
                      {selectedImageDetail.peerReviews.map((review) => (
                        <article key={review.id} className="rounded-2xl bg-white px-4 py-3">
                          <div className="text-sm font-semibold text-slate-700">{review.reviewerName}</div>
                          <p className="mt-2 text-sm leading-6 text-slate-600">{review.comment}</p>
                        </article>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {previewImageUrl ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6" role="dialog" aria-label="图片预览">
          <div className="relative w-full max-w-5xl">
            <ZoomToolbar
              className="absolute left-0 top-0 z-20"
              onZoomOut={() => setPreviewImageScale((prev) => normalizeViewerScale(prev - 0.25))}
              onZoomIn={() =>
                setPreviewImageScale((prev) => normalizeViewerScale(prev >= 1.25 ? prev + 0.25 : 1.25))
              }
              onReset={() => setPreviewImageScale(1)}
            />
            <RotationToolbar
              className="absolute left-1/2 top-0 z-20 -translate-x-1/2"
              onRotateLeft={() => setPreviewImageRotation((prev) => normalizeViewerRotation(prev - 90))}
              onRotateRight={() => setPreviewImageRotation((prev) => normalizeViewerRotation(prev + 90))}
              onReset={() => setPreviewImageRotation(0)}
            />
            <button
              type="button"
              onClick={() => {
                setPreviewImageUrl('');
                setPreviewImageRotation(0);
                setPreviewImageScale(1);
              }}
              className="absolute right-0 top-0 z-20 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow"
            >
              关闭
            </button>
            <img
              src={previewImageUrl}
              alt="预览大图"
              className="mx-auto max-h-[85vh] rounded-[1.75rem] shadow-2xl transition-transform"
              style={{
                transform: buildViewerTransform(previewImageRotation, previewImageScale),
              }}
            />
          </div>
        </div>
      ) : null}
    </TeacherPageShell>
  );
};

export const TeacherMedalListPage: React.FC<{ records: MedalOverviewRecord[] }> = ({ records }) => {
  const [selectedStudent, setSelectedStudent] = useState<MedalOverviewRecord | null>(null);
  const totalMedals = useMemo(
    () => records.reduce((sum, student) => sum + student.medals.length, 0),
    [records]
  );

  return (
    <TeacherPageShell title="学生获得勋章" description="这里集中查看学生已经获得的勋章。">
      <section className="grid gap-4 md:grid-cols-3">
        <article className="rounded-[1.5rem] bg-slate-900 px-5 py-5 text-white shadow-sm">
          <div className="text-sm text-slate-300">获得勋章学生</div>
          <div className="mt-2 text-3xl font-black">{records.length}</div>
        </article>
        <article className="rounded-[1.5rem] bg-amber-500 px-5 py-5 text-slate-900 shadow-sm">
          <div className="text-sm text-amber-950/70">勋章总数</div>
          <div className="mt-2 text-3xl font-black">{totalMedals}</div>
        </article>
      </section>

      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        {records.length === 0 ? (
          <SectionEmpty text="当前还没有学生获得勋章。" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {records.map((student) => (
              <button
                key={student.studentId}
                type="button"
                onClick={() => setSelectedStudent(student)}
                className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-lg shadow-sm">
                    {student.avatar || '🧒'}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{student.studentName}</div>
                    <div className="text-sm text-slate-500">已获得 {student.medals.length} 枚勋章</div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {student.medals.map((medal) => (
                    <span
                      key={`${student.studentId}-${medal.badgeId}`}
                      className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-sm text-amber-900"
                    >
                      <span>{medal.badgeIcon || '🏅'}</span>
                      <span>{medal.badgeName}</span>
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {selectedStudent ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black text-slate-900">{selectedStudent.studentName}</h3>
                <p className="mt-1 text-sm text-slate-500">已获得勋章详情</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedStudent(null)}
                className="rounded-2xl border border-stone-300 px-3 py-2 text-sm font-semibold text-slate-600"
              >
                关闭
              </button>
            </div>
            <div className="mt-6 space-y-4">
              {selectedStudent.medals.map((medal) => (
                <article
                  key={`${selectedStudent.studentId}-${medal.badgeId}`}
                  className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg shadow-sm">
                      {medal.badgeIcon || '🏅'}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{medal.badgeName}</div>
                      <div className="text-sm text-slate-500">{medal.assignmentTitle}</div>
                    </div>
                  </div>
                  {medal.feedbackSummary ? (
                    <p className="mt-4 text-sm leading-7 text-slate-700">{medal.feedbackSummary}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </TeacherPageShell>
  );
};

export const TeacherPreparationPosterPage: React.FC<{ records: PreparationPosterRecord[] }> = ({ records }) => {
  const [selectedPoster, setSelectedPoster] = useState<PreparationPosterRecord | null>(null);

  return (
    <TeacherPageShell title="探险准备海报" description="这里查看学生完成探险准备后的出发海报与准备摘要。">
      <section className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">出发海报回看</h2>
            <p className="mt-1 text-sm text-slate-500">点击卡片查看完整准备详情和语音转写摘要。</p>
          </div>
          <div className="rounded-full bg-stone-100 px-3 py-1 text-sm font-semibold text-slate-600">
            {records.length} 份
          </div>
        </div>

        {records.length === 0 ? (
          <SectionEmpty text="当前还没有学生完成探险准备海报。" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {records.map((record) => (
              <button
                key={record.id}
                type="button"
                onClick={() => setSelectedPoster(record)}
                aria-label={`查看${record.studentName}的探险准备海报详情`}
                className="overflow-hidden rounded-[1.5rem] border border-stone-200 bg-stone-50 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300"
              >
                <div className="h-52 bg-[linear-gradient(135deg,#fff7ed,#eff6ff)]">
                  {record.teamPhotoUrl ? (
                    <img
                      src={getAssetUrl(record.teamPhotoUrl)}
                      alt={`${record.studentName} 的探险准备海报`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-5xl">🧳</div>
                  )}
                </div>
                <div className="space-y-3 p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg shadow-sm">
                      {record.avatar || '🧒'}
                    </div>
                  <div>
                    <div className="font-semibold text-slate-900">{record.studentName}</div>
                    <div className="text-sm text-slate-500">{record.assignmentTitle}</div>
                    <div className="mt-1 text-xs text-slate-500">{formatTeacherUploadTime(record.createdAt)}</div>
                  </div>
                </div>
                  <div className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-700">
                    <div><span className="font-semibold">场景：</span>{record.poster.sceneName}</div>
                    <div className="mt-1 line-clamp-2"><span className="font-semibold">装备：</span>{record.poster.equipmentNames.join('、')}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {selectedPoster ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/45 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black text-slate-900">{selectedPoster.studentName}</h3>
                <p className="mt-1 text-sm text-slate-500">探险准备详情</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPoster(null)}
                className="rounded-2xl border border-stone-300 px-3 py-2 text-sm font-semibold text-slate-600"
              >
                关闭
              </button>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)]">
              <div className="overflow-hidden rounded-[1.75rem] bg-[linear-gradient(135deg,#fff7ed,#eff6ff)]">
                {selectedPoster.teamPhotoUrl ? (
                  <img
                    src={getAssetUrl(selectedPoster.teamPhotoUrl)}
                    alt={`${selectedPoster.studentName} 的出发海报大图`}
                    className="h-full max-h-[70vh] w-full object-contain"
                  />
                ) : (
                  <div className="flex min-h-[320px] items-center justify-center text-7xl">🧳</div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">上传时间</div>
                  <div className="mt-2 text-base font-semibold text-slate-900">
                    {formatTeacherUploadTime(selectedPoster.createdAt)}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">队友</div>
                  <div className="mt-2 text-base font-semibold text-slate-900">
                    {selectedPoster.poster.teammateNames.join('、')}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">场景</div>
                  <div className="mt-2 text-base font-semibold text-slate-900">
                    {selectedPoster.poster.sceneName}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">装备</div>
                  <div className="mt-2 text-base font-semibold text-slate-900">
                    {selectedPoster.poster.equipmentNames.join('、')}
                  </div>
                </div>
                <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                  <div className="text-sm font-semibold text-slate-500">语音转写摘要</div>
                  <p className="mt-3 text-sm leading-7 text-slate-700">{selectedPoster.poster.voiceSummary}</p>
                </div>
                <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5">
                  <div className="text-sm font-semibold text-amber-700">{AI_NAME}出发寄语</div>
                  <p className="mt-3 text-sm leading-7 text-amber-950">{selectedPoster.poster.blessing}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </TeacherPageShell>
  );
};
